import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, QrCode, Star, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";

const HeroSection = ({ onOpenModal }) => {
  const { t } = useTranslation();
  
  return (
    <section 
      className="relative pt-24 lg:pt-32 pb-16 lg:pb-24 overflow-hidden hero-gradient"
      data-testid="hero-section"
    >
      {/* Background decorations */}
      <div className="absolute top-40 left-10 w-72 h-72 bg-indigo-200 rounded-full blur-3xl opacity-30" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-200 rounded-full blur-3xl opacity-30" />

      <div className="container-main relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 border border-indigo-100 mb-6"
            >
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span className="text-sm font-medium text-indigo-700">{t('hero.badge')}</span>
            </motion.div>

            {/* Headline */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-headline"
            >
              {t('hero.headline')}{" "}
              <span className="gradient-text">{t('hero.headlineHighlight')}</span>
            </h1>

            {/* Subheadline */}
            <p 
              className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed max-w-xl"
              data-testid="hero-subheadline"
            >
              {t('hero.subheadline')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={onOpenModal}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                {t('hero.cta')}
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary"
                data-testid="hero-cta-secondary"
              >
                {t('hero.watchDemo')}
              </Button>
            </div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-10 flex items-center gap-6 text-sm text-slate-500"
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Free to start</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>No monthly fees</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Setup in 5 mins</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Illustration */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              {/* Main illustration container */}
              <div className="relative bg-white rounded-3xl shadow-2xl shadow-slate-200/50 p-6 lg:p-8">
                {/* Phone mockup */}
                <div className="relative mx-auto w-64 lg:w-72">
                  <div className="bg-slate-900 rounded-[2.5rem] p-3 shadow-xl">
                    <div className="bg-white rounded-[2rem] overflow-hidden">
                      {/* Phone screen content */}
                      <div className="p-4">
                        {/* App header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                              <Star className="w-4 h-4 text-white fill-white" />
                            </div>
                            <span className="font-semibold text-sm text-slate-900">PointWise</span>
                          </div>
                          <div className="text-xs text-slate-500">My Points</div>
                        </div>

                        {/* Points display */}
                        <div className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl p-4 mb-4 text-white">
                          <p className="text-xs opacity-80 mb-1">Total Points</p>
                          <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>2,450</p>
                          <div className="flex items-center gap-1 mt-2">
                            <TrendingUp className="w-3 h-3" />
                            <span className="text-xs">+150 this week</span>
                          </div>
                        </div>

                        {/* Progress to reward */}
                        <div className="bg-amber-50 rounded-xl p-3 mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-slate-700">Next Reward</span>
                            <span className="text-xs text-amber-600 font-semibold">550 pts away</span>
                          </div>
                          <div className="w-full h-2 bg-amber-100 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: '82%' }}
                              transition={{ duration: 1, delay: 0.5 }}
                              className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full"
                            />
                          </div>
                          <p className="text-xs text-slate-500 mt-2">🎁 Free Coffee - 3000 pts</p>
                        </div>

                        {/* Scan button */}
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          className="bg-indigo-600 text-white rounded-xl p-3 flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <QrCode className="w-4 h-4" />
                          <span className="text-sm font-medium">Scan to Earn</span>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute -top-4 -right-4 lg:-right-8 bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-4 float-element"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Points Earned!</p>
                    <p className="font-semibold text-slate-900">+50 pts</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="absolute -bottom-4 -left-4 lg:-left-8 bg-white rounded-2xl shadow-lg shadow-slate-200/50 p-4 float-element stagger-2"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Star className="w-5 h-5 text-amber-600 fill-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Reward Unlocked!</p>
                    <p className="font-semibold text-slate-900">🎉 Free Dessert</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
